// ═══════════════════════════════════════
// ATLAS BEASTS — LEADERBOARDS
// Pokemon Go-style mobile overlay
// ═══════════════════════════════════════

import { useState } from 'react'

const LEADERBOARD_CATEGORIES = [
  { id: 'global', label: 'Global', icon: '🌍', color: '#FFE800' },
  { id: 'weekly', label: 'Weekly', icon: '📅', color: '#00F0FF' },
  { id: 'friends', label: 'Friends', icon: '👥', color: '#39FF14' },
  { id: 'zone', label: 'Zone', icon: '🗺️', color: '#BF00FF' },
]

const SAMPLE_PLAYERS = [
  { id: '1', name: 'DragonSlayer99', avatar: '🐉', rating: 5420, wins: 284, losses: 42, rank: 1, change: 0 },
  { id: '2', name: 'BeastMaster', avatar: '🦁', rating: 5180, wins: 256, losses: 58, rank: 2, change: 1 },
  { id: '3', name: 'VoidWalker', avatar: '👻', rating: 4950, wins: 238, losses: 65, rank: 3, change: -1 },
  { id: '4', name: 'FireStorm', avatar: '🔥', rating: 4720, wins: 212, losses: 72, rank: 4, change: 2 },
  { id: '5', name: 'IceQueen', avatar: '❄️', rating: 4490, wins: 198, losses: 81, rank: 5, change: 0 },
  { id: '6', name: 'ThunderBolt', avatar: '⚡', rating: 4260, wins: 185, losses: 89, rank: 6, change: -2 },
  { id: '7', name: 'EarthShaker', avatar: '🌍', rating: 4030, wins: 172, losses: 95, rank: 7, change: 3 },
  { id: '8', name: 'WindRider', avatar: '🌪️', rating: 3800, wins: 158, losses: 102, rank: 8, change: 1 },
  { id: '9', name: 'ShadowStrike', avatar: '🌑', rating: 3570, wins: 145, losses: 108, rank: 9, change: -1 },
  { id: '10', name: 'LightBringer', avatar: '✨', rating: 3340, wins: 132, losses: 115, rank: 10, change: 0 },
]

export default function Leaderboards() {
  const [selectedCategory, setSelectedCategory] = useState('global')
  const [selectedPlayer, setSelectedPlayer] = useState<typeof SAMPLE_PLAYERS[0] | null>(null)
  const [playerRank] = useState(42) // User's current rank

  const filteredPlayers = selectedCategory === 'friends' 
    ? SAMPLE_PLAYERS.slice(0, 5)
    : SAMPLE_PLAYERS

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Compact */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Leaderboards</h1>
        <div className="flex items-center gap-2">
          <span className="font-bold text-[10px] text-[#FFE800]" style={{ fontFamily: 'Space Mono, monospace' }}>
            Your Rank: #{playerRank}
          </span>
        </div>
      </div>

      {/* Category Filter - Horizontal scroll */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 py-3 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        {LEADERBOARD_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`font-bold text-xs px-3 py-1.5 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              selectedCategory === cat.id
                ? 'border-[#FFE800] border-opacity-60 text-[#FFE800] bg-[#FFE800] bg-opacity-5'
                : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20 hover:text-[#EAF6FF]'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {filteredPlayers.map((player, index) => (
            <LeaderboardRow 
              key={player.id} 
              player={player} 
              rank={index + 1} 
              isUser={player.id === '1'} // Simulate user being top player
              onSelect={setSelectedPlayer} 
            />
          ))}
        </div>

        {/* User's rank indicator */}
        {playerRank > 10 && (
          <div className="mt-4 p-3 rounded-lg border" style={{ 
            background: 'rgba(255,232,0,0.1)', 
            borderColor: 'rgba(255,232,0,0.3)' 
          }}>
            <p className="text-[10px] text-[#FFE800] uppercase tracking-wider mb-1" style={{ fontFamily: 'Space Mono, monospace' }}>
              Your Position
            </p>
            <div className="flex items-center gap-3">
              <span className="font-bold text-lg text-[#FFE800]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                #{playerRank}
              </span>
              <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Tap to see nearby players
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Player Detail Modal */}
      {selectedPlayer && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(2,5,16,0.9)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl border p-6 max-w-sm w-full" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <button
              onClick={() => setSelectedPlayer(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-[#8A8AA5] hover:text-[#EAF6FF]"
              style={{ border: '1px solid rgba(58,58,90,0.5)' }}
            >
              ✕
            </button>
            
            <div className="text-center mb-4">
              <span className="text-5xl mb-2 block">{selectedPlayer.avatar}</span>
              <h2 className="font-bold text-xl text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                {selectedPlayer.name}
              </h2>
              <p className="text-[10px] text-[#FFE800] uppercase mt-1" style={{ fontFamily: 'Space Mono, monospace' }}>
                Rank #{selectedPlayer.rank}
              </p>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Rating</span>
                <span className="font-bold text-lg text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{selectedPlayer.rating}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Wins</span>
                <span className="font-bold text-sm text-[#39FF14]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{selectedPlayer.wins}</span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Losses</span>
                <span className="font-bold text-sm text-[#FF4800]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{selectedPlayer.losses}</span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Win Rate</span>
                <span className="font-bold text-sm text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                  {Math.round((selectedPlayer.wins / (selectedPlayer.wins + selectedPlayer.losses)) * 100)}%
                </span>
              </div>
            </div>

            <button
              onClick={() => setSelectedPlayer(null)}
              className="w-full py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF 0%, #00F0FF80 100%)',
                color: '#020510',
                fontFamily: 'Space Mono, monospace',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function LeaderboardRow({ player, rank, isUser, onSelect }: { 
  player: typeof SAMPLE_PLAYERS[0]; 
  rank: number; 
  isUser: boolean; 
  onSelect: (player: typeof SAMPLE_PLAYERS[0]) => void 
}) {
  const getRankColor = (rank: number) => {
    if (rank === 1) return '#FFE800' // Gold
    if (rank === 2) return '#C0C0C0' // Silver
    if (rank === 3) return '#CD7F32' // Bronze
    return '#8A8AA5'
  }

  const rankColor = getRankColor(rank)
  const changeColor = player.change > 0 ? '#39FF14' : player.change < 0 ? '#FF4800' : '#8A8AA5'

  return (
    <button
      onClick={() => onSelect(player)}
      className={`rounded-xl border p-3 transition-all active:scale-95 hover:scale-105 text-left flex items-center gap-3 ${
        isUser ? 'ring-2' : ''
      }`}
      style={{ 
        background: isUser ? 'rgba(255,232,0,0.1)' : 'rgba(13,13,21,0.95)', 
        borderColor: isUser ? 'rgba(255,232,0,0.5)' : 'rgba(58,58,90,0.5)',
        boxShadow: isUser ? '0 0 12px rgba(255,232,0,0.2)' : 'none',
      }}
    >
      {/* Rank */}
      <div className="flex-shrink-0">
        <span 
          className="text-lg font-bold w-8 h-8 flex items-center justify-center rounded-lg"
          style={{ 
            color: rankColor,
            background: `${rankColor}15`,
            fontFamily: 'Rajdhani, system-ui',
          }}
        >
          {rank}
        </span>
      </div>

      {/* Avatar */}
      <div className="flex-shrink-0">
        <span className="text-3xl">{player.avatar}</span>
      </div>

      {/* Info */}
      <div className="flex-1">
        <p className="font-bold text-sm text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
          {player.name}
          {isUser && <span className="ml-2 text-[10px] text-[#FFE800]" style={{ fontFamily: 'Space Mono, monospace' }}>YOU</span>}
        </p>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[10px] text-[#FFE800]" style={{ fontFamily: 'Space Mono, monospace' }}>
            {player.rating} ELO
          </span>
          <span className="text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
            {player.wins}W / {player.losses}L
          </span>
        </div>
      </div>

      {/* Change */}
      <div className="flex-shrink-0">
        <span 
          className="text-xs font-bold"
          style={{ 
            color: changeColor,
            fontFamily: 'Space Mono, monospace',
          }}
        >
          {player.change > 0 ? `↑${player.change}` : player.change < 0 ? `↓${Math.abs(player.change)}` : '-'}
        </span>
      </div>
    </button>
  )
}
